import { createContext, use } from 'react';

import { Permission } from '@/api';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { unique } from '@/utils/array';

const useAvailableSettingsScopes = () => {
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  return (
    currentSession?.roles
      .reduce(
        (acc, userRole) => {
          // If the role is scoped to an organization, make sure it's the current one
          if (userRole.organizationId && userRole.organizationId !== currentOrganization?.id) {
            return acc;
          }

          // Organization-level role
          if (userRole.organizationId && !userRole.entityId) {
            const existingScope = acc.find(
              (scope) => scope.organizationId === userRole.organizationId && !scope.entityId
            );

            const permissions = (
              ['manage_organization', 'manage_entities', 'manage_users', 'manage_billing'] as const
            ).filter((permission) => userRole.role.permissions.includes(permission));

            if (existingScope) {
              existingScope.permissions = unique([...existingScope.permissions, ...permissions]);
            } else {
              acc.push({
                scope: 'organization' as const,
                organizationId: userRole.organizationId,
                entityId: null,
                name: userRole.organization!.name,
                permissions,
              });
            }
          }

          // Entity-level role
          if (userRole.entityId) {
            const existingScope = acc.find(
              (scope) =>
                scope.organizationId === userRole.organizationId &&
                scope.entityId === userRole.entityId
            );

            const permissions = (['manage_entity', 'manage_users'] as const).filter((permission) =>
              userRole.role.permissions.includes(permission)
            );

            if (existingScope) {
              existingScope.permissions = unique([...existingScope.permissions, ...permissions]);
            } else {
              acc.push({
                scope: 'entity' as const,
                organizationId: userRole.organizationId,
                entityId: userRole.entityId,
                name: userRole.entity!.name,
                permissions,
              });
            }
          }

          return acc;
        },
        [] as ({
          name: string;
          permissions: Permission[];
        } & (
          | {
              scope: 'organization';
              organizationId: string;
              entityId: null;
            }
          | {
              scope: 'entity';
              organizationId: string | null;
              entityId: string;
            }
        ))[]
      )
      .filter((scope) => scope !== null)
      .sort((a, b) => {
        // Organization scopes first
        if (a.scope === 'organization' && b.scope === 'entity') {
          return -1;
        }
        if (a.scope === 'entity' && b.scope === 'organization') {
          return 1;
        }

        return 0;
      }) ?? []
  );
};

type SettingsScope = ReturnType<typeof useAvailableSettingsScopes>[number];

export type SettingsOrganizationScope = Extract<SettingsScope, { scope: 'organization' }>;

export const useSettingsOrganizationScope = (): SettingsOrganizationScope | null =>
  useAvailableSettingsScopes().find(
    (scope): scope is SettingsOrganizationScope => scope.scope === 'organization'
  ) ?? null;

type SettingsContextType = {
  scope: SettingsOrganizationScope | null;
};

export const SettingsContext = createContext<SettingsContextType>({} as SettingsContextType);

export const useSettingsScope = () => use(SettingsContext);
